import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { MAP as MAP_KEY } from 'react-google-maps/lib/constants';

import Map from 'components/Shared/Map';
import { MapToolbar } from 'components/Map/MapToolbar';
import MapWrapperScroll from 'components/Map/MapWrapperScroll';
import MapWrapperGuidelines from 'components/Map/MapWrapperGuidelines';
import MapWrapperOuter from 'components/Map/MapWrapperOuter';
import MapWrapperInner from 'components/Map/MapWrapperInner';
import { BREAKPOINTS, SCAFFOLDING, TRANSITION, SIDECONTROLS, MAPTOOLBAR } from 'theme';
import { PANELS, getActivePanel } from 'modules/navigation/activePanel';
import {
    changePreviewZoom,
    changePreviewLocation,
    clearPreviewLocation,
    getPreviewZoom,
} from 'modules/navigation/preview';
import { setLocation } from 'components/LocationSettings/LocationForm';
import { getMapSize, MAP_DIMENSION_UNIT } from 'modules/size';
import { getActiveMarker } from 'modules/navigation/activeMarker';
import { roundLatLng } from 'utils/maps';
import { setActiveMarkerLocation } from 'components/MarkerSettings/MarkerForm';
import notify from 'utils/notify';
import { getMapTileLanguage } from 'store';
import { getGoogleMapsProps } from 'components/Map/MapWrapperHelper';
import { setMapType } from 'components/AdvancedSettings/AdvancedForm';

const StyledMapWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: ${SCAFFOLDING.COLUMN_WIDTH(5)};
    z-index: 0;
    background-color: #eee;
    transition: left ${TRANSITION.LENGTH_LONG} ${TRANSITION.BEZIER};

    @media (min-width: ${`${BREAKPOINTS.lg}px`}) {
        left: ${SCAFFOLDING.COLUMN_WIDTH(4)};
    }

    @media (max-width: ${`${BREAKPOINTS.md - 1}px`}) {
        display: none;
    }

    ${ifProp(
        'fullscreen',
        css`
            left: 0 !important;
        `
    )}
`;

class MapWrapper extends React.Component {
    handleMapLoad = (map) => {
        this.map = map;
    };

    isLocationPanelActive() {
        return this.props.activePanel === PANELS.LOCATION;
    }

    isMarkerPanelActive() {
        return this.props.activePanel === PANELS.MARKER;
    }

    isAdvancedSettingsPanelActive() {
        return this.props.activePanel === PANELS.ADVANCED_SETTINGS;
    }

    handleLocationChange = () => {
        const currentLocation = this.getCurrentLocation();
        this.props.onPreviewLocationChange(currentLocation);
        if (this.isLocationPanelActive()) {
            this.props.onLocationChange(currentLocation);
        }
    };

    handleMarkerDragEnd = (markerId, location) => {
        if (this.isMarkerPanelActive()) {
            this.props.onActiveMarkerLocationChange(location);
        }
    };

    handleMapTypeChange = () => {
        if (this.isAdvancedSettingsPanelActive()) {
            this.props.onMapTypeChange(this.map.getMapTypeId());
        }
    };

    handleMapClick = (e) => {
        if (!this.isMarkerPanelActive()) return;

        const { activeMarker, onActiveMarkerLocationChange } = this.props;
        if (!activeMarker || !activeMarker.location) {
            onActiveMarkerLocationChange(roundLatLng(e.latLng));
            notify.success('Marker location set. You can click and drag the marker to adjust the position.');
        }
    };

    getCurrentLocation() {
        const center = this.map.getCenter();
        return {
            zoom: this.map.getZoom(),
            center: roundLatLng(center),
        };
    }

    handleScrollUpdate = () => {
        // Fixes Safari issue of the scroll position not resetting
        const scrollEl = ReactDOM.findDOMNode(this.mapWrapperScroll); // eslint-disable-line react/no-find-dom-node
        const childEl = ReactDOM.findDOMNode(this.mapWrapperGuidelines); // eslint-disable-line react/no-find-dom-node

        if (scrollEl && childEl) {
            if (scrollEl.offsetWidth >= childEl.offsetWidth) {
                scrollEl.scrollLeft = 0;
            }

            if (scrollEl.offsetHeight >= childEl.offsetHeight) {
                scrollEl.scrollTop = 0;
            }
        }
    };

    handleGuideDimen = (dimen, zoom, units) => {
        const gutter = parseInt(SCAFFOLDING.GUTTER, 10);
        let outerWidth = dimen * zoom;

        if (units === MAP_DIMENSION_UNIT.PERCENT) {
            const width = outerWidth;
            const offset = (width / 100) * gutter * 2;
            outerWidth = `calc(${width}${units} - ${offset}${MAP_DIMENSION_UNIT.PIXEL})`;
        } else {
            outerWidth += units;
        }
        return outerWidth;
    };

    handleOuterDimen = (dimen, zoom, units) => {
        let innerDimen = dimen * zoom;
        if (units === MAP_DIMENSION_UNIT.PERCENT && innerDimen > 100) {
            innerDimen = 100;
        }
        return innerDimen + units;
    };

    handleInnerDimen = (zoom) => {
        return (1 / zoom) * 100 + MAP_DIMENSION_UNIT.PERCENT;
    };

    handleOnChangePreviewZoom = (previewZoom) => {
        if (previewZoom < 0.1) {
            previewZoom = 0.1;
        } else if (previewZoom > 1) {
            previewZoom = 1;
        }

        previewZoom = Math.round(previewZoom * 100) / 100;
        this.props.onChangePreviewZoom(previewZoom);
    };

    handleOnFitToScreen = () => {
        // TODO: Find a better way to include the scrollbar
        // TODO: Allow em and rem units
        const wrapper = ReactDOM.findDOMNode(this.mapWrapper); // eslint-disable-line react/no-find-dom-node
        const offset = parseInt(SCAFFOLDING.GUTTER_FACTOR(2), 10) + 20; // Extra 20 to the offset to include the scrollbar
        let wrapperWidth = 0;
        let wrapperHeight = 0;
        let mapWidth = 100; // Assume 100% width initially
        let mapHeight = 100; // Assume 100% height initially
        let zoomWidth = 1;
        let zoomHeight = 1;

        if (wrapper) {
            wrapperWidth = wrapper.offsetWidth;
            wrapperHeight = wrapper.offsetHeight;
        }

        // Calculate the zoom value for the width
        if (this.props.previewWidthUnit === MAP_DIMENSION_UNIT.PIXEL) {
            mapWidth = wrapperWidth - offset - parseInt(SIDECONTROLS.BUTTON.WIDTH, 10);
        }
        if (this.props.previewWidth > mapWidth) {
            zoomWidth = mapWidth / this.props.previewWidth;
        }

        // Calculate the zoom value for the height
        if (this.props.previewHeightUnit === MAP_DIMENSION_UNIT.PIXEL) {
            mapHeight = wrapperHeight - offset - parseInt(MAPTOOLBAR.HEIGHT, 10);
        }
        if (this.props.previewHeight > mapHeight) {
            zoomHeight = mapHeight / this.props.previewHeight;
        }

        // Set the zoom to the smaller value of width or height
        this.handleOnChangePreviewZoom(Math.min(zoomWidth, zoomHeight));
    };

    handleMapResize = () => {
        if (this.map && this.map.context) {
            const mapInstance = this.map.context[MAP_KEY];
            if (mapInstance) {
                window.google.maps.event.trigger(mapInstance, 'resize');
                mapInstance.panTo(this.props.center);
            }
        }
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        const propsChanged = (...props) => props.some((prop) => this.props[prop] !== nextProps[prop]);
        const trigger = window.requestAnimationFrame || window.setTimeout;

        if (propsChanged('previewWidth', 'previewWidthUnit', 'previewHeight', 'previewHeightUnit')) {
            trigger(this.handleMapResize);
        } else if (propsChanged('previewZoom')) {
            trigger(this.handleScrollUpdate);
        } else if (propsChanged('fullscreen')) {
            // Fullscreen toggle has to wait until the side menu
            // is completely collapsed before resizing
            setTimeout(this.handleMapResize, TRANSITION.LENGTH_LONG_VALUE);
        }
    }

    render() {
        const {
            googleMapURL,
            center,
            zoom,
            options,
            markers,
            activeMarker,
            previewWidth,
            previewWidthUnit,
            previewHeight,
            previewHeightUnit,
            previewZoom,
            onSnapToCenter,
            fullscreen,
            activePanel,
        } = this.props;

        return (
            <StyledMapWrapper
                fullscreen={fullscreen}
                ref={(el) => {
                    this.mapWrapper = el;
                }}
            >
                <MapWrapperScroll
                    ref={(el) => {
                        this.mapWrapperScroll = el;
                    }}
                >
                    <MapWrapperGuidelines
                        ref={(el) => {
                            this.mapWrapperGuidelines = el;
                        }}
                        active={this.isLocationPanelActive()}
                        width={this.handleGuideDimen(previewWidth, previewZoom, previewWidthUnit)}
                        height={this.handleGuideDimen(previewHeight, previewZoom, previewHeightUnit)}
                    >
                        <MapWrapperOuter
                            width={this.handleOuterDimen(previewWidth, previewZoom, previewWidthUnit)}
                            height={this.handleOuterDimen(previewHeight, previewZoom, previewHeightUnit)}
                        >
                            <MapWrapperInner
                                width={this.handleInnerDimen(previewZoom)}
                                height={this.handleInnerDimen(previewZoom)}
                                previewZoom={previewZoom}
                            >
                                <Map
                                    googleMapURL={googleMapURL}
                                    center={center}
                                    zoom={zoom}
                                    onLoad={this.handleMapLoad}
                                    onClick={this.handleMapClick}
                                    onPan={this.handleLocationChange}
                                    onZoom={this.handleLocationChange}
                                    options={options}
                                    activeMarker={activeMarker}
                                    activePanel={activePanel}
                                    markers={markers}
                                    onMarkerDragEnd={this.handleMarkerDragEnd}
                                    onMapTypeChange={this.handleMapTypeChange}
                                />
                            </MapWrapperInner>
                        </MapWrapperOuter>
                    </MapWrapperGuidelines>
                </MapWrapperScroll>
                {/* Map Zoom */}
                <MapToolbar
                    active
                    previewZoom={previewZoom}
                    onChangePreviewZoom={this.handleOnChangePreviewZoom}
                    onFitToScreen={this.handleOnFitToScreen}
                    onSnapToCenter={onSnapToCenter}
                />
            </StyledMapWrapper>
        );
    }
}

const mapStateToProps = (state) => {
    const googleProps = getGoogleMapsProps(state, getMapTileLanguage());
    const mapSize = getMapSize(state);
    return {
        ...googleProps,
        activePanel: getActivePanel(state),
        previewZoom: getPreviewZoom(state),
        previewWidth: mapSize.width,
        previewWidthUnit: mapSize.widthUnit,
        previewHeight: mapSize.height,
        previewHeightUnit: mapSize.heightUnit,
        activeMarker: getActiveMarker(state),
        fullscreen: state.navigation.fullscreen,
    };
};

const mapDispatchToProps = {
    onChangePreviewZoom: changePreviewZoom,
    onLocationChange: setLocation,
    onPreviewLocationChange: changePreviewLocation,
    onActiveMarkerLocationChange: setActiveMarkerLocation,
    onSnapToCenter: clearPreviewLocation,
    onMapTypeChange: setMapType,
};

export default connect(mapStateToProps, mapDispatchToProps)(MapWrapper);
